import { Fragment, useMemo } from "react";
import type { CSSProperties } from "react";
import { Icon } from "@/components/Icon/Icon";
import { buildGroups, groupAgg } from "./grouping";
import type { GroupKey, SubstanceGroup } from "./grouping";
import type { SubstanceRow } from "./_model";
import { USAGE_META } from "./_model";
import { TypeGlyph } from "./atoms/TypeGlyph";
import { FamilyDot } from "./atoms/FamilyDot";
import { UsageTags } from "./atoms/UsageTag";
import { IdentifierGroup } from "./atoms/IdentifierGroup";
import { CompositionCell } from "./atoms/CompositionCell";
import { StockCell } from "./atoms/StockCell";
import { RowKebab } from "./atoms/RowKebab";

// ── console preset constants ──────────────────────────────────────────────
const COLS = ["check", "name", "cas", "fema", "ec", "composition", "usage", "stock", "kebab"] as const;
type ColKey = typeof COLS[number];

// Composition track (6th) is sized to fit the readiness labels ("Needs composition")
// on a single line.
const GRID = "28px minmax(186px,1.35fr) 114px 64px 104px 150px 150px minmax(140px,1fr) 28px";

// Rendered height of SlColHeader ("7px"*2 padding + ~1px border + 9.5px mono cap).
// Folder headers stick just below it. Keep in sync if SlColHeader padding/border changes.
const COL_HEADER_HEIGHT = 28;

const SL_COL_LABEL: Record<ColKey, string> = {
  check: "", name: "Substance",
  cas: "CAS", fema: "FEMA", ec: "EC No.",
  composition: "Composition", usage: "Used as", stock: "Inventory", kebab: "",
};

const SL_COL_ALIGN: Partial<Record<ColKey, CSSProperties["textAlign"]>> = { kebab: "center", check: "center" };

// ── context ───────────────────────────────────────────────────────────────
interface TableCtx {
  selected: Set<string>;
  onSelect: (id: string) => void;
  onView: (s: SubstanceRow) => void;
  copied: string | null;
  copy: (value: string, key: string) => void;
  groupBy: GroupKey;
}

// ── one cell ──────────────────────────────────────────────────────────────
function SlCell({ col, s, ctx }: { col: ColKey; s: SubstanceRow; ctx: TableCtx }) {
  // console preset: dense is always true
  const dense = true;
  switch (col) {
    case "check":
      return (
        <label onClick={(e) => e.stopPropagation()} style={{ display: "inline-flex", cursor: "pointer", padding: 4 }}>
          <input type="checkbox" checked={ctx.selected.has(s.id)} onChange={() => ctx.onSelect(s.id)}
            style={{ accentColor: "var(--brand-green)", width: 14, height: 14, cursor: "pointer" }}/>
        </label>
      );
    case "name":
      return (
        <div className="hstack" style={{ gap: dense ? 7 : 9, minWidth: 0 }}>
          <TypeGlyph type={s.type} size={dense ? 14 : 16}/>
          <div className="vstack" style={{ gap: 1, minWidth: 0 }}>
            <div className="hstack" style={{ gap: 7, minWidth: 0 }}>
              <span style={{
                fontSize: dense ? 12.5 : 14, fontWeight: 600, color: "var(--ink)",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                letterSpacing: "-0.005em",
              }}>{s.name}</span>
            </div>
            <div className="hstack" style={{ gap: 8, minWidth: 0 }}>
              <span className="hstack" style={{ gap: 5 }}>
                <FamilyDot family={s.family} color={s.familyColor} label={s.familyLabel}/>
                <span className="mono" style={{ fontSize: 10, color: "var(--ink-4)", letterSpacing: 0 }}>{s.code}</span>
              </span>
            </div>
          </div>
        </div>
      );
    case "cas":
      return <IdentifierGroup kind="CAS" values={s.cas} copied={ctx.copied} onCopy={ctx.copy}/>;
    case "fema":
      return <IdentifierGroup kind="FEMA" values={s.fema} copied={ctx.copied} onCopy={ctx.copy}/>;
    case "ec":
      return <IdentifierGroup kind="EC" values={s.ec} copied={ctx.copied} onCopy={ctx.copy}/>;
    case "composition":
      return <CompositionCell substance={s} onView={ctx.onView} dense={dense}/>;
    case "usage":
      return <UsageTags usage={s.usage} size="sm" iconOnly={true}/>;
    case "stock":
      return <StockCell substance={s} dense={dense}/>;
    case "kebab":
      return <RowKebab/>;
    default:
      return null;
  }
}

// ── one substance row ─────────────────────────────────────────────────────
function SlRow({ s, ctx, indent }: { s: SubstanceRow; ctx: TableCtx; indent?: boolean }) {
  const dense = true;
  const rowPad = "5px 12px";
  const selected = ctx.selected.has(s.id);
  return (
    <div
      onClick={() => ctx.onView(s)}
      className="sl-row" data-sel={selected ? "1" : "0"}
      style={{
        display: "grid", gridTemplateColumns: GRID, alignItems: "center",
        columnGap: dense ? 10 : 14, padding: rowPad,
        paddingLeft: indent ? (dense ? 26 : 30) : undefined,
        borderBottom: "1px solid var(--line-2)",
        cursor: "pointer", position: "relative",
      }}>
      {indent && (
        <span style={{ position: "absolute", left: dense ? 13 : 15, top: 0, bottom: 0, width: 1, background: "var(--line)" }}/>
      )}
      {COLS.map(col => (
        <div key={col} style={{ minWidth: 0, justifySelf: SL_COL_ALIGN[col] === "center" ? "center" : "stretch", textAlign: SL_COL_ALIGN[col] || "left" }}>
          <SlCell col={col} s={s} ctx={ctx}/>
        </div>
      ))}
    </div>
  );
}

// ── column header row ──────────────────────────────────────────────────────
function SlColHeader() {
  const dense = true;
  return (
    <div style={{
      display: "grid", gridTemplateColumns: GRID, alignItems: "center",
      columnGap: dense ? 10 : 14, padding: dense ? "7px 12px" : "9px 14px",
      position: "sticky", top: 0, zIndex: 5,
      background: "var(--panel-2)", borderBottom: "1px solid var(--line)",
    }}>
      {COLS.map(col => (
        <div key={col} className="mono" style={{
          fontSize: 9.5, fontWeight: 600, letterSpacing: "0.09em", textTransform: "uppercase",
          color: "var(--ink-4)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          textAlign: SL_COL_ALIGN[col] || "left",
        }}>{SL_COL_LABEL[col]}</div>
      ))}
    </div>
  );
}

// ── folder header (for grouped views) ─────────────────────────────────────
function SlFolderHeader({ group, expanded, onToggle, single }: {
  group: SubstanceGroup;
  expanded: boolean;
  onToggle: () => void;
  single: boolean;
}) {
  const agg = groupAgg(group.items);
  const isUsage = !!USAGE_META[group.key as keyof typeof USAGE_META];
  const dense = true;
  const accent = group.accent || "var(--brand-green)";

  // console variant: bold is always false (usage groups render as quieter rows)
  const bold = false;

  return (
    <div
      onClick={single ? undefined : onToggle}
      style={{
        display: "flex", alignItems: "center", gap: 11,
        padding: bold ? "12px 16px" : (dense ? "6px 12px" : "9px 14px"),
        paddingLeft: bold ? 0 : (dense ? 12 : 14),
        background: bold ? "var(--bg-sunk)" : "var(--panel-2)",
        borderBottom: "1px solid var(--line)",
        borderTop: bold ? "1px solid var(--line)" : "none",
        cursor: single ? "default" : "pointer",
        position: "sticky", top: COL_HEADER_HEIGHT, zIndex: 4,
      }}>
      {bold && <span style={{ width: 4, alignSelf: "stretch", background: accent, flexShrink: 0 }}/>}
      {bold && <span style={{ width: 14 }}/>}

      {/* toggle / folder glyph */}
      <span style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        width: 20, height: 20, color: single ? "var(--ink-mute)" : "var(--ink-3)",
        transition: "transform 140ms", transform: (!single && expanded) ? "none" : "none",
      }}>
        {single
          ? <Icon name="folder" size={14}/>
          : <span style={{ display: "inline-flex", transform: expanded ? "rotate(90deg)" : "none", transition: "transform 140ms" }}><Icon name="chevron-r" size={13}/></span>}
      </span>

      {isUsage && (
        <span style={{ display: "inline-flex", color: accent }}>
          <Icon name={USAGE_META[group.key as keyof typeof USAGE_META].icon as Parameters<typeof Icon>[0]["name"]} size={bold ? 17 : 14}/>
        </span>
      )}
      {!isUsage && group.accent && (
        <span style={{ width: 9, height: 9, borderRadius: 2, background: group.accent, flexShrink: 0 }}/>
      )}

      <div className="hstack" style={{ gap: 9, minWidth: 0, flex: 1, alignItems: "baseline" }}>
        {group.sublabel && !isUsage && (
          <span className="mono" style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--ink-4)" }}>{group.sublabel}</span>
        )}
        <span style={{
          fontSize: bold ? 15 : (dense ? 12 : 13), fontWeight: 700, color: "var(--ink)",
          letterSpacing: "-0.005em",
          fontFamily: (group.sublabel && !isUsage) ? "var(--font-mono)" : undefined,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>{group.label}</span>
        {isUsage && bold && (
          <span style={{ fontSize: 11.5, color: "var(--ink-4)" }}>{USAGE_META[group.key as keyof typeof USAGE_META].blurb}</span>
        )}
        {single && (
          <span style={{ fontSize: 10.5, color: "var(--ink-mute)", fontStyle: "italic" }}>· 1 substance</span>
        )}
      </div>

      {/* aggregate roll-up */}
      <div className="hstack" style={{ gap: dense ? 9 : 13, flexShrink: 0 }}>
        {agg.needs > 0 && (
          <span className="hstack" style={{ gap: 5, fontSize: 11, color: "#8a6a1e", fontWeight: 500 }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#c9a93b" }}/>
            {agg.needs} need data
          </span>
        )}
        {agg.withStock > 0 && (
          <span className="hstack" style={{ gap: 5, fontSize: 11, color: "var(--ink-4)" }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: agg.inStock > 0 ? "var(--brand-lime)" : "var(--line-strong)" }}/>
            {agg.inStock}/{agg.withStock} in stock
          </span>
        )}
        {!isUsage && (
          <span className="mono" style={{ fontSize: 10, color: "var(--ink-4)", letterSpacing: 0 }}>
            {agg.compounds > 0 && `${agg.compounds}C`}{agg.compounds > 0 && agg.mixtures > 0 ? " · " : ""}{agg.mixtures > 0 && `${agg.mixtures}M`}
          </span>
        )}
        <span className="mono" style={{
          fontSize: 11, fontWeight: 700, letterSpacing: 0, color: "var(--ink-2)",
          padding: "1px 8px", borderRadius: 999, background: "var(--panel)", border: "1px solid var(--line)",
        }}>{agg.count}</span>
      </div>
    </div>
  );
}

// ── the table ──────────────────────────────────────────────────────────────
interface SubstanceTableProps {
  items: SubstanceRow[];
  groupBy: GroupKey;
  expanded: Set<string>;
  onToggleGroup: (key: string) => void;
  ctx: TableCtx;
  /** Pre-computed groups from the parent. When provided, skips the internal
   *  buildGroups call (avoids a double grouping pass over large real datasets). */
  groups?: SubstanceGroup[];
}

export function SubstanceTable({ items, groupBy, expanded, onToggleGroup, ctx, groups: propGroups }: SubstanceTableProps) {
  const groups = useMemo(
    () => propGroups ?? buildGroups(items, groupBy),
    [propGroups, items, groupBy],
  );

  if (items.length === 0) {
    return (
      <div style={{ padding: "70px 20px", textAlign: "center", color: "var(--ink-4)" }}>
        <div style={{ marginBottom: 10 }}><Icon name="search" size={26}/></div>
        <div style={{ fontSize: 13, color: "var(--ink-3)" }}>No substances match these filters.</div>
        <div className="mono" style={{ fontSize: 11, marginTop: 4 }}>Try clearing a filter or the search.</div>
      </div>
    );
  }

  return (
    <div style={{ minWidth: 1080 }}>
      <SlColHeader/>
      {groups.map(group => {
        if (group.flat) {
          // group-by None — flat list, no folder chrome
          return (
            <Fragment key={group.key}>
              {group.items.map(s => <SlRow key={s.id} s={s} ctx={ctx}/>)}
            </Fragment>
          );
        }
        if (group.single) {
          // a group of one is not a folder — it's just a row
          return (
            <Fragment key={group.key}>
              {group.items.map(s => <SlRow key={s.id} s={s} ctx={ctx}/>)}
            </Fragment>
          );
        }
        const isOpen = expanded.has(group.key);
        return (
          <div key={group.key}>
            <SlFolderHeader group={group}
              expanded={isOpen} single={false}
              onToggle={() => onToggleGroup(group.key)}/>
            {isOpen && group.items.map(s => <SlRow key={s.id} s={s} ctx={ctx} indent/>)}
          </div>
        );
      })}
    </div>
  );
}
